import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import _ from 'lodash'
import { PlusIcon, XCircleIcon } from '@heroicons/react/24/outline'
import { Controller, useFieldArray } from 'react-hook-form'
import { twMerge as mergeClassNames } from 'tailwind-merge'

// Components
import { Button } from '../Button'
import { Select } from '../Select'
import { TextInput } from '../TextInput'

// Utils & Styles
import {
  INCLUSION_OPTIONS,
  MULTI_VALUE_MERGE_FIELDS,
  OPERATOR_OPTIONS,
} from '../../utils/constants'
import { configureSelectedMergeFieldOptions } from '../../utils/helpers'

/**
 * Hard cap on the rules in one logic group. Shared by every caller: badge
 * element visibility, badge list lines, session access and kiosk pop-ups.
 * The rule list scrolls, so the builder stays usable at the cap.
 */
const MAX_LOGIC_FIELDS = 25

const DEFAULT_LOGIC = {
  inclusionLogic: INCLUSION_OPTIONS[0],
  mergeField: null,
  operator: null,
  value: null,
}

const MergeFieldLogicFields = ({
  admissionItems = null,
  baseField,
  categories,
  control,
  clearErrors,
  errors,
  getOperatorOptions = null,
  getValues,
  loading = false,
  mergeFields = {},
  mergeFieldOptions,
  oilTypeOptions = null,
  operatorOptions = OPERATOR_OPTIONS,
  register,
  setValue,
  sessions = null,
  watch,
}) => {
  // When the operator list is filtered per merge field (e.g. session rules
  // forbid contains on option-backed scalar fields), use that; otherwise
  // fall back to the static `operatorOptions` list.
  const operatorOptionsFor = (mergeFieldId) =>
    getOperatorOptions ? getOperatorOptions(mergeFieldId) : operatorOptions
  const { fields, append, remove } = useFieldArray({
    control,
    name: baseField,
    rules: { required: true },
  })

  const inclusionLogicButtons = useMemo(() => {
    if (fields.length >= MAX_LOGIC_FIELDS) {
      return (
        <p className="text-xs italic text-gray-600">
          Maximum of {MAX_LOGIC_FIELDS} conditions reached.
        </p>
      )
    }

    if (fields.length === 1) {
      return (
        <div className="flex flex-row gap-4">
          <Button
            className="border-gray-200 bg-gray-200 text-gray-700 hover:bg-gray-300"
            icon={<PlusIcon className="h-5 stroke-gray stroke-2" />}
            label="AND"
            onClick={() => {
              append(DEFAULT_LOGIC)
              setValue('inclusionLogic', INCLUSION_OPTIONS[0])
            }}
          />

          <Button
            className="border-gray-200 bg-gray-200 text-gray-700 hover:bg-gray-300"
            icon={<PlusIcon className="h-5 stroke-gray stroke-2" />}
            label="OR"
            onClick={() => {
              append({ ...DEFAULT_LOGIC, inclusionLogic: INCLUSION_OPTIONS[1] })
              setValue('inclusionLogic', INCLUSION_OPTIONS[1])
              setValue(`${baseField}.0.inclusionLogic`, INCLUSION_OPTIONS[1])
            }}
          />
        </div>
      )
    }

    if (getValues(`${baseField}.0.inclusionLogic`)?.id === 'AND') {
      return (
        <Button
          className="border-gray-200 bg-gray-200 text-gray-700 hover:bg-gray-300"
          icon={<PlusIcon className="h-5 stroke-gray stroke-2" />}
          label="AND"
          onClick={() => append(DEFAULT_LOGIC)}
        />
      )
    }

    return (
      <Button
        className="border-gray-200 bg-gray-200 text-gray-700 hover:bg-gray-300"
        icon={<PlusIcon className="h-5 stroke-gray stroke-2" />}
        label="OR"
        onClick={() => append({ ...DEFAULT_LOGIC, inclusionLogic: INCLUSION_OPTIONS[1] })}
      />
    )
  }, [fields, watch(`${baseField}.0.inclusionLogic`)])

  const renderValueField = (key, i) => {
    const mergeField = watch(`${key}.mergeField`)
    const operator = watch(`${key}.operator`)
    const isContainsOperator = operator?.id === 'contains' || operator?.id === 'not_contains'
    const isPopulatedOperator = operator?.id === 'populated' || operator?.id === 'not_populated'
    const isMultiValueField = MULTI_VALUE_MERGE_FIELDS.includes(mergeField?.id)
    const options = configureSelectedMergeFieldOptions(
      mergeField,
      mergeFields,
      categories,
      admissionItems,
      sessions,
      oilTypeOptions,
    )

    // Free-text TextInput for `contains`/`not_contains` on scalar fields
    // (so authors can type partial labels and let validateLogic do
    // substring-on-resolved-label). Multi-value fields like sessions get
    // the picker even for contains, since "contains" there means set
    // membership by id, and storing a typed UUID is not a real workflow.
    if ((isContainsOperator && !isMultiValueField) || !options || options.length === 0) {
      return (
        <TextInput
          className="rounded-2xl border-gray-550 py-2.5 pr-4 placeholder:font-normal placeholder:text-gray-600 focus-within:border-purple"
          data-testid={`${key}.value`}
          disabled={loading || isPopulatedOperator}
          error={errors[baseField] && errors[baseField][i]?.value && 'This field is required'}
          fullWidth
          id={`${key}.value`}
          inputStyles="rounded-none"
          name={`${key}.value`}
          nunito
          label="Value"
          placeholder="Value"
          {...register(`${key}.value`, {
            required: !isPopulatedOperator,
          })}
        />
      )
    }

    const selectedId = getValues(`${key}.value`)?.id || getValues(`${key}.value`)
    const value = _.find(options, (o) => o.id === selectedId) || null

    return (
      <Controller
        name={`${key}.value`}
        control={control}
        render={() => (
          <Select
            className="rounded-2xl border-gray-550 py-2.5 pr-4 placeholder:font-normal placeholder:text-gray-600 focus-within:border-purple"
            data-testid={`${key}.value`}
            disabled={loading || isPopulatedOperator}
            error={errors[baseField] && errors[baseField][i]?.value && 'This field is required'}
            fullWidth
            id={`${key}.value`}
            label="Value"
            name={`${key}.value`}
            nunito
            onChange={(e) => {
              setValue(`${key}.value`, e)
              clearErrors(`${key}.value`)
            }}
            options={options}
            placeholder="Select an Option"
            search
            style={{ flex: true, width: '100%' }}
            value={value}
          />
        )}
        rules={{
          required: !isPopulatedOperator,
        }}
      />
    )
  }

  return (
    <>
      <div className="flex w-full flex-col gap-4">
        {fields.length > 1 && (
          <Controller
            name="inclusionLogic"
            control={control}
            render={({ field: { onChange, value } }) => (
              <Select
                className="rounded-2xl border-gray-550 py-2.5 pr-4 placeholder:font-normal placeholder:text-gray-600 focus-within:border-purple"
                data-testid="inclusionLogic"
                disabled={loading}
                error={errors.inclusionLogic && 'This field is required'}
                fullWidth
                id="inclusionLogic"
                name="inclusionLogic"
                nunito
                onChange={(d) => {
                  onChange(d)

                  // Update the inclusion logic for each logic field
                  const logic = INCLUSION_OPTIONS.find((o) => o.id === d.id)
                  _.forEach(fields, (l, i) => {
                    setValue(`${baseField}.${i}.inclusionLogic`, logic)
                  })
                }}
                options={INCLUSION_OPTIONS}
                label="Inclusion Logic"
                placeholder="Select a Inclusion Logic"
                style={{ flex: true, width: '100%' }}
                value={value}
              />
            )}
            rules={{ required: true }}
          />
        )}

        <div className="flex max-h-[400px] w-full flex-col gap-4 overflow-y-auto">
          {_.map(fields, (field, i) => {
            const key = `${baseField}.${i}`
            return (
              <div className="flex w-full flex-row" key={field.id}>
                <div
                  className={mergeClassNames(
                    'flex h-full w-9 shrink-0 flex-col items-center justify-center gap-1',
                    i === 0 && 'w-0',
                    i !== 0 && 'mr-4',
                  )}
                >
                  {i > 0 && (
                    <span className="rounded-lg bg-gray-200 p-1 text-xs font-semibold text-gray-700">
                      {getValues('inclusionLogic')?.label}
                    </span>
                  )}

                  {i !== 0 && (
                    <Button
                      icon={<XCircleIcon className="h-6 stroke-inherit" />}
                      className="rounded-full stroke-red p-0 hover:stroke-red-600"
                      iconOnly
                      onClick={() => remove(i)}
                    />
                  )}
                </div>

                <div className="flex w-full flex-row gap-4">
                  <Controller
                    name={`${key}.mergeField`}
                    control={control}
                    render={({ field: { onChange, value } }) => (
                      <Select
                        className="rounded-2xl border-gray-550 py-2.5 pr-4 placeholder:font-normal placeholder:text-gray-600 focus-within:border-purple"
                        data-testid={`${key}.mergeField`}
                        disabled={loading}
                        error={
                          errors[baseField] &&
                          errors[baseField][i]?.mergeField &&
                          'This field is required'
                        }
                        fullWidth
                        id={`${key}.mergeField`}
                        label="Merge Field"
                        name={`${key}.mergeField`}
                        nunito
                        onChange={(e) => {
                          // Reset logic field value
                          setValue(`${key}.value`, null)

                          // If the new merge field restricts which operators
                          // are allowed and the current operator is no longer
                          // valid, reset it so the user re-picks rather than
                          // saving an invalid combination.
                          const allowedOps = operatorOptionsFor(e?.id)
                          const currentOp = getValues(`${key}.operator`)
                          if (currentOp && !_.find(allowedOps, (o) => o.id === currentOp.id)) {
                            setValue(`${key}.operator`, null)
                          }

                          // Update the merge field
                          onChange(e)
                        }}
                        options={mergeFieldOptions}
                        placeholder="Select a Merge Field"
                        search
                        style={{ flex: true, width: '100%' }}
                        value={value}
                      />
                    )}
                    rules={{ required: true }}
                  />

                  <Controller
                    name={`${key}.operator`}
                    control={control}
                    render={({ field: { onChange, value } }) => (
                      <Select
                        className="rounded-2xl border-gray-550 py-2.5 pr-4 placeholder:font-normal placeholder:text-gray-600 focus-within:border-purple"
                        data-testid={`${key}.operator`}
                        disabled={loading}
                        error={
                          errors[baseField] &&
                          errors[baseField][i]?.operator &&
                          'This field is required'
                        }
                        fullWidth
                        id={`${key}.operator`}
                        label="Operator"
                        name={`${key}.operator`}
                        nunito
                        onChange={(e) => {
                          const prev = getValues(`${key}.operator`)
                          const containsOps = ['contains', 'not_contains']
                          const wasContains = containsOps.includes(prev?.id)
                          const isContains = containsOps.includes(e?.id)
                          const mergeFieldId = watch(`${key}.mergeField`)?.id
                          const isMultiValue = MULTI_VALUE_MERGE_FIELDS.includes(mergeFieldId)

                          // Reset value when switching between value-input
                          // shapes (text vs. select). Multi-value fields use
                          // the picker for both is/is_not and contains/
                          // not_contains, so no reset is needed there.
                          // A populated / not populated rule has no value, so
                          // clear one picked earlier rather than keep it hidden.
                          const isPopulated = ['populated', 'not_populated'].includes(e?.id)
                          if (isPopulated || (!isMultiValue && wasContains !== isContains)) {
                            setValue(`${key}.value`, null)
                            clearErrors(`${key}.value`)
                          }

                          onChange(e)
                        }}
                        options={operatorOptionsFor(watch(`${key}.mergeField`)?.id)}
                        placeholder="Select an Operator"
                        style={{ flex: true, width: '100%' }}
                        value={value}
                      />
                    )}
                    rules={{ required: true }}
                  />

                  {renderValueField(key, i)}
                </div>
              </div>
            )
          })}
        </div>
      </div>
      <div className="flex w-full flex-row justify-center">{inclusionLogicButtons}</div>
    </>
  )
}

MergeFieldLogicFields.propTypes = {
  admissionItems: PropTypes.object,
  baseField: PropTypes.string.isRequired,
  categories: PropTypes.object,
  control: PropTypes.object,
  clearErrors: PropTypes.func,
  errors: PropTypes.object,
  getOperatorOptions: PropTypes.func,
  getValues: PropTypes.func,
  loading: PropTypes.bool,
  mergeFields: PropTypes.object,
  mergeFieldOptions: PropTypes.array.isRequired,
  oilTypeOptions: PropTypes.object,
  operatorOptions: PropTypes.array,
  register: PropTypes.func,
  sessions: PropTypes.object,
  setValue: PropTypes.func,
  watch: PropTypes.func,
}

export default MergeFieldLogicFields
